import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getAuthenticatedApiClient } from "../../../api/apiClient";
import type {
  GrantTraderAccessRequest,
  UpdateTraderAccessExpiryRequest
} from "../../../api/Api";

export const traderAccessKeys = {
  all: ["traderAccess"] as const,
  list: (counterpartyId: string, includeRevoked: boolean) =>
    [...traderAccessKeys.all, "list", counterpartyId, includeRevoked] as const
};

export const useTraderGrants = (
  counterpartyId: string | undefined,
  includeRevoked: boolean
) => {
  return useQuery({
    queryKey: traderAccessKeys.list(counterpartyId ?? "", includeRevoked),
    enabled: !!counterpartyId,
    queryFn: async () => {
      const apiClient = await getAuthenticatedApiClient();
      const response = await apiClient.counterpartyTraderGrants.list({
        entityId: counterpartyId!,
        includeRevoked
      });
      return response.data;
    }
  });
};

type MutationCallbacks = {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
};

const invalidateGrants = (
  queryClient: ReturnType<typeof useQueryClient>,
  counterpartyId: string
) => {
  queryClient.invalidateQueries({
    queryKey: [...traderAccessKeys.all, "list", counterpartyId]
  });
};

export const useGrantTraderAccess = (options?: MutationCallbacks) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (body: GrantTraderAccessRequest) => {
      const apiClient = await getAuthenticatedApiClient();
      const response = await apiClient.counterpartyTraderGrants.grant(body);
      return response.data;
    },
    onSuccess: (_data, variables) => {
      invalidateGrants(queryClient, variables.entityId);
      options?.onSuccess?.();
    },
    onError: (error) => {
      options?.onError?.(error as Error);
    }
  });
};

export const useRevokeTraderAccess = (options?: MutationCallbacks) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (vars: { entityId: string; userObjectId: string }) => {
      const apiClient = await getAuthenticatedApiClient();
      const response = await apiClient.counterpartyTraderGrants.revoke(vars);
      return response.data;
    },
    onSuccess: (_data, variables) => {
      invalidateGrants(queryClient, variables.entityId);
      options?.onSuccess?.();
    },
    onError: (error) => {
      options?.onError?.(error as Error);
    }
  });
};

export const useUpdateTraderAccessExpiry = (options?: MutationCallbacks) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (body: UpdateTraderAccessExpiryRequest) => {
      const apiClient = await getAuthenticatedApiClient();
      const response =
        await apiClient.counterpartyTraderGrants.updateExpiry(body);
      return response.data;
    },
    onSuccess: (_data, variables) => {
      invalidateGrants(queryClient, variables.entityId);
      options?.onSuccess?.();
    },
    onError: (error) => {
      options?.onError?.(error as Error);
    }
  });
};
